/* eslint-disable react/jsx-indent */
import { useTranslation } from 'react-i18n-lite'
import { useSearchParams } from 'react-router-dom'

import {
  Button,
  ExplicitSelectionButton,
  YesObject
} from '@yes.technology/react-toolkit'

import { concatenateFields } from 'shared/utils/object/concatenateFields'

import {
  ACTIONS_COLUMN_MAX_WIDTH,
  SELECTION_COLUMN_MAX_WIDTH
} from '../constants'
import useSelection from '../hooks/useSelection'
import useTableContext from '../hooks/useTableContext'

import { DataTableSkeletonDots } from '../DataTable.styles'

import { ColumnsPosition, FieldSettings } from 'shared/types'
import TableCell from './TableCell/TableCell'
import { Row } from './style'

export enum TableRowBackgroundColor {
  EVEN = '#ffffff',
  ODD = '#f7f8fa',
  ACTIVE = '#c7deff'
}

export type TableRowBaseProps = {
  isEvenRow?: boolean
  insertionIndex?: string
  isLoading?: boolean
  fieldSettings?: FieldSettings
}

export type TableRowProps =
  | ({
      rowObject: YesObject
    } & TableRowBaseProps)
  | ({
      rowObject?: never
    } & TableRowBaseProps)

export default function TableRow({
  rowObject,
  isEvenRow,
  insertionIndex,
  isLoading,
  fieldSettings
}: TableRowProps) {
  const {
    idObjectClass,
    columnsNames,
    columnsLayouts,
    actionColumns,
    actionColumnsPosition,
    pagination,
    totalObjects,
    sortOptions,
    objects,
    actionButton,
    mode,
    objectclassQueryString,
    informational,
    embedded,
    concatFields,
    hideRowActionButtons,
    customActionButtons,
    allowHighlightSelectedRow,
    highlightedRows,
    onBeforeViewObject
  } = useTableContext()

  const { onChangeSelection, getCheckedOption, getOption, selectedRows } =
    useSelection()

  const { t } = useTranslation()
  const objectId = rowObject?.uuid || insertionIndex || ''

  const [searchParams] = useSearchParams()
  const isUniselection = searchParams.has('uniselection')

  const showColumn =
    !embedded ||
    actionButton?.type === 'document' ||
    !informational ||
    !!customActionButtons

  const yesObject: YesObject = rowObject as YesObject

  const concatenatedFields = concatFields
    ? concatenateFields(concatFields, yesObject)
    : yesObject

  const isActiveObjectRow = allowHighlightSelectedRow
    ? searchParams.get('object-uuid') === objectId
    : false

  const getRowBackgroundColor = () => {
    if (isActiveObjectRow) {
      return TableRowBackgroundColor.ACTIVE
    }

    if (isEvenRow) {
      return TableRowBackgroundColor.EVEN
    }

    return TableRowBackgroundColor.ODD
  }

  const rowBackgroundColor = getRowBackgroundColor()

  const getActionCells = (position: ColumnsPosition) => {
    const className = `text-center fixed ${position}`

    if (!concatenatedFields) {
      return [
        <td
          key='view'
          className={className}
          style={{ maxWidth: ACTIONS_COLUMN_MAX_WIDTH }}
        >
          <Button disabled={!isLoading} variant='secondary' heightClass='small'>
            {isLoading ? <DataTableSkeletonDots /> : t('data-tables.view')}
          </Button>
        </td>
      ]
    }

    const visibleActionColumns = showColumn ? actionColumns || [] : []

    return visibleActionColumns.map(
      ({ title, component: ActionComponent }, index) => (
        <td
          className={className}
          key={title || index}
          style={{ maxWidth: ACTIONS_COLUMN_MAX_WIDTH }}
        >
          {isLoading ? (
            <Button variant='secondary' heightClass='small'>
              <DataTableSkeletonDots />
            </Button>
          ) : (
            <ActionComponent
              informational={informational}
              object={concatenatedFields}
              version={concatenatedFields.version}
              target={actionButton?.target}
              type={actionButton?.type}
              sitemodel={actionButton?.sitemodel}
              customActionButton={customActionButtons?.[objectId]}
              idObjectClass={idObjectClass}
              pagination={pagination}
              totalObjects={totalObjects}
              sortOptions={sortOptions}
              objects={objects}
              objectclassQueryString={objectclassQueryString}
              mode={mode}
              isActiveObjectRow={isActiveObjectRow}
              onBeforeViewObject={onBeforeViewObject}
            />
          )}
        </td>
      )
    )
  }

  const actionCells = (position: ColumnsPosition, hide?: boolean) =>
    hide ? [] : getActionCells(position)

  const contentCells = columnsNames.map((columnName) => {
    const dateOptions = fieldSettings?.[columnName]?.date_options
    const columnLayout = columnsLayouts.find(
      (layout) => layout.name === columnName
    )
    const isPreviewEnabledByColumnLayout = columnLayout?.enable_preview ?? false

    const isPreviewEnabledByFieldSettings =
      fieldSettings?.[columnName]?.enable_preview ?? false

    const enablePreview =
      isPreviewEnabledByColumnLayout || isPreviewEnabledByFieldSettings

    return isLoading ? (
      <td key={columnName}>
        <DataTableSkeletonDots />
      </td>
    ) : (
      <TableCell
        key={columnName}
        value={concatenatedFields ? concatenatedFields[columnName] : ''}
        columnName={columnName}
        objectUuidOrIndex={objectId}
        dateOptions={dateOptions}
        rowBackgroundColor={rowBackgroundColor}
        enablePreview={enablePreview}
      />
    )
  })

  const selectionCell = !informational && mode === 'view' && (
    <td
      className='text-center fixed first'
      key='selection-cell'
      style={{ maxWidth: SELECTION_COLUMN_MAX_WIDTH }}
    >
      {isLoading ? (
        <DataTableSkeletonDots />
      ) : (
        <ExplicitSelectionButton
          id='selection-cell'
          heightClass='extraSmall'
          optionLabelsPosition='top'
          direction='horizontal'
          selectionType={isUniselection ? 'uniselection' : 'multiselection'}
          checkedOption={
            getCheckedOption(concatenatedFields?.uuid || '') as any
          }
          options={concatenatedFields ? [getOption(concatenatedFields)] : []}
          required={false}
          onChange={onChangeSelection}
        />
      )}
    </td>
  )

  const isSelectedRow = !!selectedRows[objectId]

  const isHighlightedRow = highlightedRows?.includes(objectId)

  return (
    <Row
      key={objectId}
      isSelected={isSelectedRow}
      isHighlighted={isHighlightedRow}
      className={isActiveObjectRow ? 'active' : undefined}
    >
      {actionColumnsPosition === 'first'
        ? [
            selectionCell,
            ...actionCells(
              !!selectionCell ? 'second' : 'first',
              hideRowActionButtons
            ),
            ...contentCells
          ]
        : [
            selectionCell,
            ...contentCells,
            ...actionCells('last', hideRowActionButtons)
          ]}
    </Row>
  )
}
